import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  Search,
  FolderKanban,
  Home,
  UserRound,
  MapPin,
  ArrowRight,
  CheckCircle2,
  Clock3,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import "./OssProjectsPage.css";
import {
  getCompletedProgress,
  getLatestCompletedStep,
  getDisplayProjectStatus,
} from "./renovationProjectProgress";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function statusIcon(status) {
  if (status === "COMPLETED" || status === "CLOSED") return CheckCircle2;
  if (status === "CANCELLED") return AlertCircle;
  return Clock3;
}

function statusClass(status) {
  return String(status || "PLANNING")
    .toLowerCase()
    .replaceAll("_", "-");
}

const PROJECTS_PER_PAGE = 4;

export default function OssProjectsPage() {
  const { t } = useTranslation("oss-projects");
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const [reloadKey, setReloadKey] = React.useState(0);

  const hasLoadedOnceRef = React.useRef(false);
  const [query, setQuery] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("ALL");
  const [error, setError] = React.useState("");

  const [currentPage, setCurrentPage] = React.useState(1);

  React.useEffect(() => {
    let alive = true;

    async function loadProjects() {
      const hasExistingData = hasLoadedOnceRef.current;

      try {
        if (hasExistingData) {
          setIsRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const token = await getAccessTokenSilently();

        const res = await fetch(`${API_BASE}/api/oss/projects/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error("Failed to load projects.");
        }

        const data = await res.json();

        if (!alive) {
          return;
        }

        setProjects(Array.isArray(data) ? data : data.results || []);

        hasLoadedOnceRef.current = true;
      } catch (err) {
        if (!alive) {
          return;
        }

        console.error("Failed to load OSS projects:", err);

        if (!hasExistingData) {
          setError(t("errors.loadFailed"));
        }
      } finally {
        if (alive) {
          setLoading(false);
          setIsRefreshing(false);
        }
      }
    }

    loadProjects();

    return () => {
      alive = false;
    };
  }, [getAccessTokenSilently, reloadKey, t]);

  const filters = React.useMemo(
    () => [
      { key: "ALL", label: t("filters.all") },
      { key: "PLANNING", label: t("filters.planning") },
      { key: "IN_PROGRESS", label: t("filters.inProgress") },
      { key: "COMPLETED", label: t("filters.completed") },
      { key: "CLOSED", label: t("filters.closed") },
    ],
    [t],
  );

  const filteredProjects = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return projects.filter((project) => {
      const matchesFilter =
        activeFilter === "ALL" ||
        getDisplayProjectStatus(project) === activeFilter;

      const searchableText = [
        project.code,
        project.title,
        project.property_address,
        project.property?.address,
        project.homeowner_name,
        project.homeowner?.full_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesFilter && searchableText.includes(normalizedQuery);
    });
  }, [projects, activeFilter, query]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE),
  );

  const paginatedProjects = React.useMemo(() => {
    const start = (currentPage - 1) * PROJECTS_PER_PAGE;
    return filteredProjects.slice(start, start + PROJECTS_PER_PAGE);
  }, [filteredProjects, currentPage]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [query, activeFilter]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  function handleRefresh() {
    if (loading || isRefreshing) {
      return;
    }

    setReloadKey((previous) => previous + 1);
  }

  return (
    <main className="oss-projects-page">
      <section className="oss-projects-hero">
        <div>
          <span className="oss-projects-eyebrow">
            <FolderKanban size={15} />
            {t("eyebrow")}
          </span>

          <h1>{t("title")}</h1>

          <p>{t("subtitle")}</p>
        </div>
      </section>

      <section className="oss-projects-toolbar" aria-label={t("aria.toolbar")}>
        <label className="oss-projects-search">
          <Search size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("search.placeholder")}
            aria-label={t("search.aria")}
          />
        </label>

        <div className="oss-projects-filters">
          {filters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={
                activeFilter === filter.key
                  ? "oss-projects-filter is-active"
                  : "oss-projects-filter"
              }
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}

          <button
            type="button"
            className="oss-projects-refresh"
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            title={t("buttons.refresh", "Refresh projects")}
            aria-label={t("buttons.refresh", "Refresh projects")}
          >
            <RefreshCw
              size={17}
              strokeWidth={2.2}
              className={
                isRefreshing
                  ? "oss-projects-refresh__icon oss-projects-refresh__icon--spinning"
                  : "oss-projects-refresh__icon"
              }
            />
          </button>
        </div>
      </section>

      {loading && (
        <section className="oss-projects-grid">
          {Array.from({ length: 4 }).map((_, index) => (
            <article className="oss-project-card is-skeleton" key={index}>
              <div />
              <div />
              <div />
            </article>
          ))}
        </section>
      )}

      {!loading && error && (
        <section className="oss-projects-empty">
          <AlertCircle size={30} />
          <h2>{t("errors.title")}</h2>
          <p>{error}</p>
        </section>
      )}

      {!loading && !error && filteredProjects.length === 0 && (
        <section className="oss-projects-empty">
          <FolderKanban size={30} />
          <h2>{t("empty.title")}</h2>
          <p>{t("empty.description")}</p>
        </section>
      )}

      {!loading && !error && filteredProjects.length > 0 && (
        <section className="oss-projects-grid">
          {paginatedProjects.map((project, index) => {
            const progress = getCompletedProgress(project);
            const latestStep = getLatestCompletedStep(project);
            const displayProjectStatus = getDisplayProjectStatus(project);
            const StatusIcon = statusIcon(displayProjectStatus);

            const address =
              project.property_address ||
              project.property?.address ||
              t("fallback.noAddress");

            const homeowner =
              project.homeowner_name ||
              project.homeowner?.full_name ||
              t("fallback.noHomeowner");

            return (
              <article
                className="oss-project-card"
                key={project.id || project.code}
                style={{ animationDelay: `${index * 45}ms` }}
              >
                <div className="oss-project-card__top">
                  <div>
                    <h2>
                      {project.code || t("fallback.noCode")} · {address} ·{" "}
                      {homeowner}
                    </h2>

                    <div className="oss-project-card__meta">
                      <span>
                        <Home size={15} />
                        {project.title || t("fallback.noTitle")}
                      </span>

                      <span>
                        <UserRound size={15} />
                        {homeowner}
                      </span>

                      <span>
                        <MapPin size={15} />
                        {address}
                      </span>
                    </div>
                  </div>

                  <span
                    className={`oss-project-status oss-project-status--${statusClass(
                      displayProjectStatus,
                    )}`}
                  >
                    <StatusIcon size={14} />
                    {t(
                      `statuses.${displayProjectStatus}`,
                      displayProjectStatus,
                    )}
                  </span>
                </div>

                <div className="oss-project-progress">
                  <div className="oss-project-progress__header">
                    <span>{t("progress.label")}</span>
                    <strong>{progress}%</strong>
                  </div>

                  <div className="oss-project-progress__track">
                    <span style={{ width: `${progress}%` }} />
                  </div>

                  <p>
                    {latestStep
                      ? t("progress.latestCompleted", {
                          step: t(`steps.${latestStep.step_code}`),
                        })
                      : t("progress.noneCompleted")}
                  </p>
                </div>

                <div className="oss-project-card__footer">
                  <span>
                    {t("updated")}{" "}
                    {project.updated_at
                      ? new Date(project.updated_at).toLocaleDateString("en-GB")
                      : t("fallback.notAvailable")}
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/oss/projects/${project.id}/property-information`,
                      )
                    }
                  >
                    {t("buttons.openProject")}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}
      {!loading && !error && filteredProjects.length > PROJECTS_PER_PAGE && (
        <nav
          className="oss-projects-pagination"
          aria-label={t("pagination.aria")}
        >
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          >
            {t("pagination.previous")}
          </button>

          <span>
            {t("pagination.pageOf", {
              page: currentPage,
              total: totalPages,
            })}
          </span>

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
          >
            {t("pagination.next")}
          </button>
        </nav>
      )}
    </main>
  );
}
